import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, Loader2, XCircle, AlertCircle, Car } from 'lucide-react';
import { searchPassengers, initializeUrbanrideApi } from '../../services/urbanrideService';

/**
 * UrbanrideProfileTab - Tab component for linking Urbanride passenger profiles
 *
 * Shows a single profile section based on the current system mode (test or production).
 * Users don't see or interact with the mode - it's determined automatically by the system.
 */
const UrbanrideProfileTab = ({
  urbanridePaxIdTest,
  urbanridePaxIdProduction,
  executiveFirstName,
  executiveLastName,
  onUpdateTestProfile,
  onUpdateProductionProfile
}) => {
  // Urbanride configuration state
  const [urbanrideConfigured, setUrbanrideConfigured] = useState(false);
  const [urbanrideMode, setUrbanrideMode] = useState('production'); // Default to production
  const [configLoading, setConfigLoading] = useState(true);

  // Single profile state (based on current mode)
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [linkedProfile, setLinkedProfile] = useState(null);

  const dropdownRef = useRef(null);

  // Check Urbanride API configuration on mount
  useEffect(() => {
    const checkUrbanrideConfig = async () => {
      setConfigLoading(true);
      try {
        const result = await initializeUrbanrideApi();
        setUrbanrideConfigured(result.configured);
        setUrbanrideMode(result.mode || 'production');
      } catch (e) {
        setUrbanrideConfigured(false);
      } finally {
        setConfigLoading(false);
      }
    };
    checkUrbanrideConfig();
  }, []);

  // Initialize linked profile based on current mode
  useEffect(() => {
    const currentPaxId = urbanrideMode === 'test' ? urbanridePaxIdTest : urbanridePaxIdProduction;

    if (currentPaxId) {
      setLinkedProfile({
        id: currentPaxId,
        firstName: executiveFirstName,
        lastName: executiveLastName
      });
    } else {
      setLinkedProfile(null);
    }
  }, [urbanrideMode, urbanridePaxIdTest, urbanridePaxIdProduction, executiveFirstName, executiveLastName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search Urbanride passengers
  const handleSearch = async (query) => {
    setSearch(query);

    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      setSearchLoading(true);
      setShowDropdown(true);
      const result = await searchPassengers({ input: query });
      const passengers = result?.data?.passengers || result?.passengers || [];
      setSearchResults(Array.isArray(passengers) ? passengers : []);
    } catch (e) {
      console.error('Urbanride search failed:', e);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Select a profile - automatically uses current mode
  const handleSelectProfile = (profile) => {
    setLinkedProfile(profile);

    // Update the appropriate profile based on current mode
    if (urbanrideMode === 'test') {
      onUpdateTestProfile(profile.id);
    } else {
      onUpdateProductionProfile(profile.id);
    }

    setSearch('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  // Remove linked profile
  const handleRemoveProfile = () => {
    setLinkedProfile(null);

    // Clear the appropriate profile based on current mode
    if (urbanrideMode === 'test') {
      onUpdateTestProfile(null);
    } else {
      onUpdateProductionProfile(null);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl p-8 overflow-hidden relative">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/40 via-transparent to-gray-50/40 pointer-events-none" />

      <div className="relative flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/30">
          <Car className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Urbanride Profile</h2>
          <p className="text-slate-500 mt-1">Link passenger profile for faster chauffeur bookings</p>
        </div>
      </div>

      {configLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mr-3" />
          <span className="text-sm text-slate-500">Checking Urbanride configuration...</span>
        </div>
      ) : !urbanrideConfigured ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Urbanride API Not Configured
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Configure your Urbanride API keys in your profile settings to link passenger profiles.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 mb-4">
            Link this executive to their Urbanride passenger profile. When booking chauffeur services,
            this profile will be used to pre-fill passenger information.
          </p>

          {/* Single Profile Section */}
          <div className="border border-slate-200 rounded-xl p-4 bg-white">
            {linkedProfile ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        {linkedProfile.firstName || linkedProfile.first_name} {linkedProfile.lastName || linkedProfile.last_name}
                        {linkedProfile.email && <span className="font-normal text-green-700"> ({linkedProfile.email})</span>}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        Profile linked successfully
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveProfile}
                    className="text-green-600 hover:text-green-800 p-1 hover:bg-green-100 rounded-lg transition-colors"
                    title="Remove link"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                    className="w-full pl-10 pr-10 py-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Search by name or email..."
                  />
                  {searchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>

                {showDropdown && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchLoading ? (
                      <div className="p-4 text-center text-slate-500 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Searching...
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((profile) => (
                        <button
                          key={profile.id}
                          type="button"
                          onClick={() => handleSelectProfile(profile)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {profile.title && `${profile.title} `}
                                {profile.firstName || profile.first_name} {profile.lastName || profile.last_name}
                                {profile.vip && (
                                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full font-semibold">VIP</span>
                                )}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {profile.email}
                                {profile.email && profile.mobile && ' | '}
                                {profile.mobile}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))
                    ) : search.length >= 2 ? (
                      <div className="p-4 text-center text-slate-500 text-sm">
                        No matching profiles found
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UrbanrideProfileTab;
